import { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Empty,
    Form,
    Progress,
    Row,
    Select,
    Tag,
    Typography,
} from 'antd';
import {
    ArrowLeftOutlined,
    ClockCircleOutlined,
    CloudUploadOutlined,
    DownloadOutlined,
    FileTextOutlined,
    HistoryOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import { Controller } from 'react-hook-form';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';

import { IGiveaway } from '../../../../interfaces';
import useImportTaxeAutoForm from './useImportTaxeAutoForm';
import { formatDateStringToDateAndTime } from '../../../../utils/Conversores';
import RaffleBlacklist from '../RaffleBlacklist';
import PageHeader from '../../../../components/PageHeader';
import { ThemeContext } from '../../../../contexts/ThemeContext';

const { Text } = Typography;

interface IProps {
    giveaway?: IGiveaway;
    createdGiveaway?: IGiveaway;
    onSubmit?: (user: IGiveaway) => Promise<void>;
}

export default function ImportTaxeAutoForm({ giveaway, createdGiveaway }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const {
        refIsEditing,
        handleSubmitForm,
        handleSubmit,
        control,
        isSubmiting,
        errors,
        giveawaysAuto,
        clearErrors,
        impostosMunicipais,
        ACCEPTED_FILE_TYPES,
        dummyRequest,
        handleChangeFile,
        files,
        taxAutoFile,
        handleClickDownloadFile,
    } = useImportTaxeAutoForm({ giveaway, createdGiveaway });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Breadcrumb Header Card */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 10px rgba(0, 0, 0, 0.03)',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/impostos/auto">Impostos (IPTU)</Link>,
                        },
                        {
                            title: 'Importar Impostos',
                        },
                    ]}
                />
            </div>

            <PageHeader
                icon={<CloudUploadOutlined />}
                iconBg="linear-gradient(135deg, #1677ff 0%, #722ed1 100%)"
                title="Importar Impostos (IPTU)"
                tag={<Tag color="blue">Importação em lote</Tag>}
                subtitle="Faça o upload dos arquivos de arrecadação do IPTU (.txt ou .json) vinculando ao sorteio desejado para processamento e geração de bilhetes."
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/impostos/auto')}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            borderRadius: 8,
                            fontWeight: 500,
                        }}
                    >
                        Voltar para Impostos
                    </Button>
                }
            />

            {/* Form Section */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 12,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 12px rgba(0, 0, 0, 0.03)',
                    padding: 24,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            background: isDark ? 'rgba(22, 119, 255, 0.2)' : '#e6f4ff',
                            color: '#1677ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 16,
                        }}
                    >
                        <FileTextOutlined />
                    </div>
                    <div>
                        <h2
                            style={{
                                margin: 0,
                                fontSize: 16,
                                fontWeight: 700,
                                color: isDark ? '#f0f0f0' : '#1e293b',
                            }}
                        >
                            Parâmetros da Importação
                        </h2>
                        <span style={{ fontSize: 13, color: isDark ? '#8c8c8c' : '#64748b' }}>
                            Selecione o tributo, o sorteio vigente e os arquivos para processar
                        </span>
                    </div>
                </div>

                <Form
                    onFinish={handleSubmit(handleSubmitForm)}
                    autoComplete="off"
                    layout="vertical"
                >
                    <Row gutter={20}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Tipo de Imposto</span>}
                                required
                                validateStatus={errors.tax_type ? 'error' : 'success'}
                                help={errors.tax_type?.message}
                            >
                                <Controller
                                    name="tax_type"
                                    control={control}
                                    rules={{ required: { value: true, message: 'Por favor, selecione o tipo do imposto!' } }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            placeholder="Selecione o imposto"
                                            defaultValue="IPTU"
                                            size="large"
                                            style={{ width: '100%' }}
                                        >
                                            {impostosMunicipais.map((imposto) => (
                                                <Select.Option key={imposto} value={imposto}>
                                                    {imposto}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Sorteio Vinculado</span>}
                                required
                                validateStatus={errors.raffle_auto_id ? 'error' : 'success'}
                                help={errors.raffle_auto_id?.message}
                            >
                                <Controller
                                    name="raffle_auto_id"
                                    control={control}
                                    rules={{ required: { value: true, message: 'Por favor, selecione o sorteio!' } }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            placeholder="Selecione o sorteio"
                                            onChange={(value) => {
                                                field.onChange(value);
                                                clearErrors('raffle_auto_id');
                                            }}
                                            disabled={refIsEditing.current}
                                            size="large"
                                            style={{ width: '100%' }}
                                        >
                                            {giveawaysAuto.map((g) => (
                                                <Select.Option key={g.id} value={g.id}>
                                                    {g.description}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600, color: '#ff4d4f' }}>Lista de Exclusão</span>}
                            >
                                <Controller
                                    name="county_id"
                                    control={control}
                                    rules={{ required: false }}
                                    render={() => <RaffleBlacklist />}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontWeight: 600 }}>Arquivos de Arrecadação</span>
                                        <Tag color="blue" style={{ borderRadius: 6, margin: 0, fontSize: 11 }}>.TXT</Tag>
                                        <Tag color="purple" style={{ borderRadius: 6, margin: 0, fontSize: 11 }}>.JSON</Tag>
                                    </div>
                                }
                                required
                                validateStatus={errors.files ? 'error' : 'success'}
                                help={errors.files?.message}
                            >
                                <Controller
                                    name="files"
                                    control={control}
                                    rules={{ required: { value: true, message: 'Por favor, selecione pelo menos um arquivo!' } }}
                                    render={({ field }) => (
                                        <Dragger
                                            {...field}
                                            fileList={files}
                                            showUploadList={true}
                                            customRequest={dummyRequest}
                                            multiple
                                            onChange={(e: UploadChangeParam<UploadFile>) =>
                                                handleChangeFile({ onChangeHookForm: field.onChange, fileList: e.fileList })
                                            }
                                            accept={ACCEPTED_FILE_TYPES.toString()}
                                            style={{
                                                padding: '24px 16px',
                                                background: isDark ? '#1a1a1a' : '#fafafa',
                                                border: `2px dashed ${isDark ? '#333' : '#d9d9d9'}`,
                                                borderRadius: 10,
                                            }}
                                        >
                                            {files && files.length ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                                    <InboxOutlined style={{ fontSize: 40, color: '#1677ff' }} />
                                                    <Text strong style={{ fontSize: 15, color: '#1677ff' }}>
                                                        {files.length} arquivo(s) selecionado(s)
                                                    </Text>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        Clique ou arraste mais arquivos para adicionar
                                                    </Text>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                                                    <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
                                                        <InboxOutlined style={{ fontSize: 44, color: '#1677ff' }} />
                                                    </p>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Text strong style={{ fontSize: 15 }}>
                                                            Clique ou arraste os arquivos de IPTU aqui
                                                        </Text>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            Suporte a arquivos múltiplos no formato .txt ou .json
                                                        </Text>
                                                    </div>
                                                </div>
                                            )}
                                        </Dragger>
                                    )}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<CloudUploadOutlined />}
                            loading={isSubmiting}
                            disabled={isSubmiting}
                            onClick={handleSubmit(handleSubmitForm)}
                            style={{
                                minWidth: 160,
                                fontWeight: 600,
                                borderRadius: 8,
                                height: 42,
                            }}
                        >
                            {isSubmiting ? 'Importando...' : 'Iniciar Importação'}
                        </Button>
                    </div>
                </Form>
            </div>

            {/* Imported Files History */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 12,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 12px rgba(0, 0, 0, 0.03)',
                    padding: 24,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: isDark ? 'rgba(82, 196, 26, 0.2)' : '#f6ffed',
                                color: '#52c41a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 16,
                            }}
                        >
                            <HistoryOutlined />
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <h2
                                    style={{
                                        margin: 0,
                                        fontSize: 16,
                                        fontWeight: 700,
                                        color: isDark ? '#f0f0f0' : '#1e293b',
                                    }}
                                >
                                    Arquivos Importados
                                </h2>
                                <Tag color="blue" style={{ borderRadius: 10, fontWeight: 600, margin: 0 }}>
                                    {taxAutoFile.length} arquivos
                                </Tag>
                            </div>
                            <span style={{ fontSize: 13, color: isDark ? '#8c8c8c' : '#64748b' }}>
                                Acompanhe o progresso do processamento e faça o download dos arquivos
                            </span>
                        </div>
                    </div>
                </div>

                {taxAutoFile.length === 0 ? (
                    <div style={{ padding: '30px 0' }}>
                        <Empty description="Nenhum arquivo importado recentemente." />
                    </div>
                ) : (
                    <Row gutter={[16, 16]}>
                        {taxAutoFile.map((file, index) => {
                            const isFinished = file.status === 'Finalizado';
                            const isError = file.status === 'Erro' || file.status === 'Falha';
                            const percent = file.import_progress ? parseFloat(file.import_progress.toFixed(1)) : 0;
                            const displayName = file.file_name?.split('-')[3] ?? file.file_name ?? `Arquivo ${index + 1}`;

                            return (
                                <Col key={file.id} xs={24} sm={12} lg={8}>
                                    <Card
                                        size="small"
                                        style={{
                                            borderRadius: 10,
                                            border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                            background: isDark ? '#1a1a1a' : '#fcfcfd',
                                        }}
                                        title={
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                                                    <Tag
                                                        color={isFinished ? 'success' : isError ? 'error' : 'processing'}
                                                        style={{ borderRadius: 6, fontWeight: 600, fontSize: 11, margin: 0 }}
                                                    >
                                                        {file.status || 'Processando'}
                                                    </Tag>
                                                    <Text
                                                        strong
                                                        ellipsis={{ tooltip: file.file_name }}
                                                        style={{ fontSize: 13 }}
                                                    >
                                                        {displayName}
                                                    </Text>
                                                </div>
                                                <Button
                                                    type="primary"
                                                    ghost
                                                    size="small"
                                                    icon={<DownloadOutlined />}
                                                    onClick={() => handleClickDownloadFile(file.file_name)}
                                                    style={{ borderRadius: 6 }}
                                                >
                                                    Download
                                                </Button>
                                            </div>
                                        }
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    Progresso da importação:
                                                </Text>
                                                <Text strong style={{ fontSize: 12, color: isFinished ? '#52c41a' : '#1677ff' }}>
                                                    {percent}%
                                                </Text>
                                            </div>

                                            <Progress
                                                percent={percent}
                                                showInfo={false}
                                                status={isFinished ? 'success' : isError ? 'exception' : 'active'}
                                                strokeColor={isFinished ? '#52c41a' : '#1677ff'}
                                            />

                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                                                <ClockCircleOutlined style={{ fontSize: 11, color: isDark ? '#666' : '#999' }} />
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    {file.created_at ? formatDateStringToDateAndTime(file.created_at) : '--'}
                                                </Text>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </div>
        </div>
    );
}
